import { useNavigate } from "@tanstack/react-router";
import { cn } from "@theprelude/core";
import {
  Button,
  FunnelIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@theprelude/ds";
import { usePopper } from "@/hooks/use-popper";
import { TopLevelFilters } from "@/lib/filter";
import { tb } from "@/lib/tracking";
import { useTopLevelFilters } from "@/queries/control-monitor";
import {
  AdditionalFilter,
  anyCloudRegionFilter,
  anyDeviceTypeFilter,
  anyDomainFilter,
  anyDurationLastSeenFilter,
  anyInboxTypeFilter,
  anyLastSeenFilter,
  anyOwnershipFilter,
  anyPlatformFilter,
  anyPrincipalNameFilter,
  anyRiskFilter,
  anyStatusFilter,
  anyUserTypeFilter,
  controlCloudAccountIdFilter,
  controlCloudInstanceIdFilter,
  controlCloudRegionFilter,
  emailFilter,
  hostNameFilter,
  macAddressFilter,
  serialNumberFilter,
} from "../filter-form/additional-filters";
import { getFilterCount } from "../filter-form/filter-form";
import { Category, TopLevelFilterForm } from "../filter-form/top-level-form";

export const ControlMonitorFilters = () => {
  const currentFilters = useTopLevelFilters();
  const popper = usePopper();

  const filterCount = Object.values(currentFilters).reduce(
    (acc, filters) => acc + getFilterCount(filters),
    0,
  );
  return (
    <Popover {...popper}>
      <PopoverTrigger asChild>
        <Button
          intent="secondary"
          icon={<FunnelIcon className="text-secondary-foreground" />}
          className={cn(
            "h-[38px]",
            tb("filter"),
            filterCount > 0 && "bg-secondary-background",
          )}
        >
          <span className="hidden md:block">Filter</span>
          {filterCount > 0 && (
            <div className="inline-flex items-center justify-center rounded-2xl bg-tertiary-background px-2 py-0 text-[0.625rem] text-white font-normal">
              {filterCount}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end">
        <ControlMonitorFilterForm
          onClose={popper.close}
          currentFilters={currentFilters}
        />
      </PopoverContent>
    </Popover>
  );
};

const availableFilters: Record<Category, AdditionalFilter[]> = {
  endpoints: [
    anyDeviceTypeFilter,
    anyDurationLastSeenFilter,
    anyLastSeenFilter,
    anyOwnershipFilter,
    anyPlatformFilter,
    anyCloudRegionFilter,
    hostNameFilter,
    serialNumberFilter,
    macAddressFilter,
    controlCloudRegionFilter,
    controlCloudAccountIdFilter,
    controlCloudInstanceIdFilter,
  ],
  users: [
    anyUserTypeFilter,
    emailFilter,
    anyDurationLastSeenFilter,
    anyLastSeenFilter,
    anyStatusFilter,
    anyRiskFilter,
  ],
  inboxes: [
    anyInboxTypeFilter,
    emailFilter,
    anyDomainFilter,
    anyPrincipalNameFilter,
  ],
};

function ControlMonitorFilterForm({
  onClose,
  currentFilters,
}: {
  onClose: () => void;
  currentFilters: TopLevelFilters;
}) {
  const navigate = useNavigate();
  return (
    <TopLevelFilterForm
      availableFilters={availableFilters}
      defaultFilters={currentFilters}
      onSubmit={(data) => {
        void navigate({
          to: "/control-monitor",
          search: (prev) => ({
            ...prev,
            filters: Object.values(data).some((f) => f?.length)
              ? data
              : undefined,
          }),
        });
      }}
      onClear={() => {
        void navigate({
          to: "/control-monitor",
          search: (prev) => ({
            ...prev,
            filters: undefined,
          }),
        });
      }}
      onClose={onClose}
    />
  );
}
